import React, { useRef } from 'react';
import styled from 'styled-components';
import { QuestRoute } from '../../api/QuestRoutes';
import { exportToPDF, formatCurrency, generateId, shortHandDate, shortHandDateTime, statusColors } from '../../utility/functions';
import Dot from '../../Dot';
import Page from '../../layout/Page';
import Table from '../../table/Table';
import { useParams } from 'react-router-dom';
import Button from '../../forms/Button';
import Card from '../../forms/Card';
import Col from '../../layout/Col';
import Title from '../../forms/Title';
import Loading from '../../Loading';
import CompanyLogo from '../../CompanyLogo';
import CompanyAddress from '../../CompanyAddress';
import GetState from '../../GetState';
import { SCREEN_MAX_WIDTH } from '../../stateProvider/StateProvider';
import Row from '../../layout/Row';

const HeaderRow = styled(Row)`
    display: flex;
    gap: 4em;
    justify-content: space-between;
    align-items: flex-start;
`
const ReportSegment = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.gray}40;
    margin-bottom: 0.5em;
    padding-bottom: 0.2em;
`
const CardWrap = styled(Card)`
    margin: 0.5em;
    min-width: 40em;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        min-width: unset;
        width: 70svw;
    }
`

const Invoice = ({ ...props }) => {

    const { invoiceId } = useParams();
    const cardRef = useRef();

    const [data] = GetState(QuestRoute.reports.invoice(invoiceId), {
        callback: e => {
            e.approvedPayment = e.cardResults.find(f => f.processingResult === 'Approved');
        }
    })

    return (
        <Page title={`Invoice Report (${invoiceId})`} icon='clipboard'>
            <Button
                disabled={!data}
                onClick={() => exportToPDF(cardRef, `Invoice (${invoiceId})`)}
            >Export</Button>

            {data ?
                <CardWrap title='Invoice' icon='clipboard'>
                    <div ref={cardRef}>
                        <HeaderRow style={{ marginBottom: '3em' }}>
                            <CompanyAddress />
                            <CompanyLogo dim={6} />
                        </HeaderRow>
                        <ReportSegment>
                            <HeaderRow wrap>
                                <Col style={{ flex: 1 }}>
                                    <HeaderRow>
                                        <span>{data.accountName}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <span>{data.email}</span>
                                    </HeaderRow>
                                </Col>
                                <Col style={{ flex: 1 }}>
                                    <HeaderRow>
                                        <b>Invoice Date</b>
                                        <span>{shortHandDate(data.invoiceDate)}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Account No.</b>
                                        <span>{data.loyaltyAccountId}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Invoice No.</b>
                                        <span>{data.invoiceId}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Invoice Status</b>
                                        <span><Dot color={statusColors[data.invoiceStatus]} /> {data.invoiceStatus}</span>
                                    </HeaderRow>
                                    {data.datePaid &&
                                        <HeaderRow>
                                            <b>Date Paid</b>
                                            <span>{shortHandDate(data.datePaid)}</span>
                                        </HeaderRow>
                                    }
                                </Col>
                            </HeaderRow>
                        </ReportSegment>

                        <ReportSegment>
                            <HeaderRow>
                                <Title>Item(s)</Title>
                            </HeaderRow>
                            <Table
                                config={{
                                    disableTableControls: true,
                                    disableHeaderSearch: true,
                                    // disableMobileView: true,
                                    data: data.items,
                                    colSpacing: [10, 40],
                                    headers: [
                                        {
                                            label: 'Qty',
                                            propKey: 'quantity',
                                        }, {
                                            label: 'Product',
                                            propKey: 'product',
                                        }, {
                                            label: 'Item ID',
                                            propKey: 'itemId',
                                        }, {
                                            label: 'SKU',
                                            propKey: 'sku',
                                        }, {
                                            label: 'Price',
                                            propKey: 'price',
                                            type: 'number'
                                        },
                                    ],
                                    renderFormat: (e) => (
                                        <tr key={generateId()}>
                                            <td>{e.quantity}</td>
                                            <td style={{ whiteSpace: 'normal' }}>{e.product}</td>
                                            <td>{e.itemId || '-'}</td>
                                            <td>{e.sku || '-'}</td>
                                            <td>{formatCurrency(e.price)}</td>
                                        </tr>
                                    )
                                }}
                            />
                        </ReportSegment>

                        {data.cardResults.length ?
                            <ReportSegment>
                                <HeaderRow>
                                    <Title>Card Result(s)</Title>
                                </HeaderRow>
                                <Table
                                    config={{
                                        disableTableControls: true,
                                        disableHeaderSearch: true,
                                        // disableMobileView: true,
                                        data: data.cardResults,
                                        colSpacing: [30, 15],
                                        headers: [
                                            {
                                                label: 'Result Date',
                                                propKey: 'resultDate',
                                            }, {
                                                label: 'Card Type',
                                                propKey: 'cardType',
                                            }, {
                                                label: 'Card Number',
                                                propKey: 'maskedCardNumber',
                                            }, {
                                                label: 'Result',
                                                propKey: 'processingResult',
                                            },
                                        ],
                                        renderFormat: (e) => (
                                            <tr key={generateId()}>
                                                <td>{shortHandDateTime(e.resultDate)}</td>
                                                <td>{e.cardType}</td>
                                                <td>{e.maskedCardNumber || '-'}</td>
                                                <td title={[e.processingResult, e.processorMessage].filter(e => e).join(' - ')}>
                                                    <Row>
                                                        <Dot color={statusColors[e.processingResult]} />
                                                        <span>
                                                            {e.processingResult}
                                                            {e.processingResult !== 'Approved' ? ' - ' + e.processorMessage : null}
                                                        </span>
                                                    </Row>
                                                </td>
                                            </tr>
                                        )
                                    }}
                                />
                            </ReportSegment> : null
                        }

                        <HeaderRow style={{ marginTop: '3em' }} wrap>
                            <Col style={{ flex: 1 }}>
                                {
                                    data.approvedPayment ?
                                        <>
                                            <HeaderRow>
                                                <b>Date</b>
                                                <span>{shortHandDateTime(data.approvedPayment.resultDate)}</span>
                                            </HeaderRow>
                                            <HeaderRow>
                                                <b>Status</b>
                                                <span>{data.approvedPayment.processingResult}</span>
                                            </HeaderRow>
                                            <HeaderRow>
                                                <b>Card Type</b>
                                                <span>{data.approvedPayment.cardType}</span>
                                            </HeaderRow>
                                            <HeaderRow>
                                                <b>Card #</b>
                                                <span>{data.approvedPayment.maskedCardNumber}</span>
                                            </HeaderRow>
                                            <HeaderRow>
                                                <b>Auth #</b>
                                                <span>{data.approvedPayment.authNumber}</span>
                                            </HeaderRow>
                                            <HeaderRow>
                                                <b>Transaction ID</b>
                                                <span>{data.transRecordId}</span>
                                            </HeaderRow>
                                        </> : null
                                }
                            </Col>

                            <Col style={{ flex: 1 }}>
                                <ReportSegment>
                                    <HeaderRow>
                                        <b>Sub Total</b>
                                        <span>{formatCurrency(data.subTotal)}</span>
                                    </HeaderRow>
                                    <HeaderRow>
                                        <b>Tax Total</b>
                                        <span>{formatCurrency(data.taxTotal ?? 0)}</span>
                                    </HeaderRow>
                                </ReportSegment>
                                <HeaderRow>
                                    <b>Total</b>
                                    <span>{formatCurrency(data.total)}</span>
                                </HeaderRow>
                            </Col>
                        </HeaderRow>
                    </div>
                </CardWrap>
                : <Loading page key='Loading' />
            }
        </Page>
    );
}

export default Invoice;